import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Megaphone, UsersRound, Bell, FileText, LogOut, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const getNavItems = (role) => {
  const items = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  ];
  if (role !== 'accountant') {
    items.push({ to: '/clients', label: 'Clients', icon: Users });
    items.push({ to: '/campaigns', label: 'Campaigns', icon: Megaphone });
    items.push({ to: '/alerts', label: 'Alerts', icon: Bell });
  }
  if (role === 'accountant' || role === 'admin') {
    items.push({ to: '/invoices', label: 'Invoices', icon: FileText });
  }
  if (role === 'admin') {
    items.push({ to: '/team', label: 'Team', icon: UsersRound });
  }
  return items;
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-56 bg-white border-r border-zinc-200 flex flex-col transition-transform duration-150 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className="h-14 flex items-center px-5 border-b border-zinc-200 gap-2.5">
          <img src="https://customer-assets.emergentagent.com/job_campaign-tracker-97/artifacts/ozya3vdh_final%20logo-01-01.jpg" alt="CampREV" className="h-8 w-8 object-contain" />
          <span className="text-lg font-black tracking-tighter text-zinc-950">CampREV</span>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {getNavItems(user?.role).map((item) => {
            const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-150 rounded-sm ${
                  isActive
                    ? 'bg-zinc-950 text-white'
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-zinc-200">
          <div className="px-3 py-2 text-xs text-zinc-400 truncate">
            {user?.email}
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-sm text-zinc-600 hover:text-red-600 hover:bg-red-50 rounded-sm"
            data-testid="logout-button"
          >
            <LogOut size={18} />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center px-4 lg:px-6 gap-4">
          <button
            className="lg:hidden text-zinc-600 hover:text-zinc-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-zinc-700" data-testid="user-name">{user?.name}</span>
            <div className="w-8 h-8 rounded-sm bg-zinc-950 text-white flex items-center justify-center text-xs font-bold">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
